/** @jsx jsx */
import { Layer, useMediaQuery, usePortalNode } from "@cd2/components/build/es6"
import { css, jsx } from "@emotion/core"
import { useManagedConfirmation } from "./useManagedConfirmation"
import ReactDOM from "react-dom"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { IComponentProp } from "@cd2/tools"
import { explodePromise } from "../utils/explodePromise"
import { useTheme } from "@cd2/theme"

export interface IDrawConfirmationProps<Res, Data = void> {
  data: Data
  dialogData: Data
  onResolve(response: Res | null): void
  registerOutsideClick(fn: () => void): void
}

export function useDrawConfirmation<Res, Data = void>({
  Component,
  drawSize = 480,
  portal,
  noPadding,
  memo,
}: {
  Component(props: IDrawConfirmationProps<Res, Data>): JSX.Element
  drawSize?:
    | number
    | {
        vw: number
      }
    | {
        pc: number
      }
  portal?: boolean
  noPadding?: boolean
  memo?: boolean
}) {
  const portalNode = usePortalNode()
  const [openModal, { showDialog, onResolve, data }] = useManagedConfirmation<Data, Res | null>()
  const outsideClickRef = useRef<() => void>()
  const registerOutsideClick = (fn: () => void) => {
    outsideClickRef.current = fn
  }

  const MemoComponent = useMemo(() => Component, [])
  const CompToUse = memo ? MemoComponent : Component
  let node = (
    <Drawer
      isOpen={showDialog}
      side="right"
      width={drawSize as any}
      onRequestClose={() => {
        outsideClickRef.current?.()
      }}
    >
      <div
        css={css`
          padding: ${noPadding ? undefined : "24px 16px 32px"};
          height: 100vh;
          overflow: auto;
          box-sizing: border-box;

          @media (min-width: 768px) {
            padding: ${noPadding ? undefined : "64px 32px"};
          }
        `}
      >
        <CompToUse
          data={data!}
          dialogData={data!}
          onResolve={onResolve}
          registerOutsideClick={registerOutsideClick}
        />
      </div>
    </Drawer>
  )

  if (portal) {
    node = ReactDOM.createPortal(node, portalNode.getNode())
  }

  return [openModal, node] as const
}

export interface IDialogueConfirmationProps<Res, Data = void> {
  data: Data
  dialogData: Data
  onResolve(response: Res): void
  registerOutsideClick: (func: () => void) => void
}

function Drawer({
  children,
  isOpen,
  onRequestClose,
  width,
  side,
}: {
  children: React.ReactNode
  isOpen: boolean
  onRequestClose: () => void
  width: number
  side: "left" | "right"
}) {
  const layer = useTheme().layer
  const isNarrow = useMediaQuery({ maxWidth: 767 })
  // Layer sizes an outer shell and an inner panel from the same layerWidth.
  // Overriding only one of them (e.g. via wrapper CSS) leaves a gap around the panel.
  const layerWidth = isNarrow ? { vw: 95 } : width ?? layer.drawer?.width ?? 300

  return (
    <Layer
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      theme={{
        animation: side === "left" ? "slideLeft" : "slideRight",
        layerWidth,
        position: "left",
        layerColor: "white",
        height: "100%",
      }}
    >
      {children}
    </Layer>
  )
}

export function useDrawConfirmation2<Res, Data = void>({
  width,
  Component,
  componentKey,
  onlyDestroyOnResolve,
}: {
  width?: IComponentProp<typeof Layer, "theme">["layerWidth"]
  Component(props: IDialogueConfirmationProps<Res, Data>): JSX.Element
  componentKey?: string
  // If true, the modal will only be destroyed when the promise is resolved
  // this is needed if you want to remove the underlying component and keep the modal
  // for example if you want to trigger a modal from a tooltip.
  onlyDestroyOnResolve?: boolean
}) {
  const outsideClickCbRef = useRef<(() => void) | null>(null)
  function registerOutsideClick(func: () => void) {
    outsideClickCbRef.current = func
  }

  const Node = useCallback(
    ({ showDialog, data, onResolve }) => (
      <Drawer
        isOpen={showDialog}
        onRequestClose={() => {
          outsideClickCbRef.current?.()
        }}
        side="right"
        width={(width ?? 480) as any}
      >
        {showDialog && (
          <div
            css={css`
              padding: 24px 16px 32px;
              height: 100vh;
              overflow: auto;
              box-sizing: border-box;

              @media (min-width: 768px) {
                padding: 64px 32px;
              }
            `}
          >
            <Component
              data={data}
              dialogData={data}
              onResolve={onResolve}
              registerOutsideClick={registerOutsideClick}
            />
          </div>
        )}
      </Drawer>
    ),
    [componentKey, width],
  )

  const modalRef = useRef<any>(null)

  useEffect(() => {
    modalRef.current = window.MANAGED_MODALS.create()

    return () => {
      if (!onlyDestroyOnResolve) {
        modalRef.current.destroy()
      }
    }
  }, [])

  const openModal = (data: Data) => {
    const exploded = explodePromise<Res>()

    const onResolve = (response: Res) => exploded.resolve(response)
    // show the modal
    modalRef.current.setComponent(Node, { showDialog: false, data, onResolve })
    setTimeout(() => modalRef.current.setProps({ showDialog: true, data, onResolve }), 10)
    exploded.promise.then(() => {
      modalRef.current.setProps({ showDialog: false })
      setTimeout(() => modalRef.current.setComponent(() => null, {}), 300)
    })

    return exploded.promise
  }

  return openModal
}
